
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';

interface UpdatePinFormProps {
  onBack: () => void;
}

const UpdatePinForm: React.FC<UpdatePinFormProps> = ({ onBack }) => {
  const { user: internalUser, updatePin } = useInternalAuth();
  const [showCurrentPin, setShowCurrentPin] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    currentPin: '',
    newPin: '',
    confirmPin: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.newPin !== formData.confirmPin) {
      toast.error('New PINs do not match');
      return;
    }

    if (formData.newPin.length < 4) {
      toast.error('PIN must be at least 4 characters long');
      return;
    }

    setLoading(true);
    
    try {
      const result = await updatePin(formData.currentPin, formData.newPin);
      if (result.success) {
        toast.success('PIN updated successfully!');
        setFormData({ currentPin: '', newPin: '', confirmPin: '' });
        onBack();
      } else {
        toast.error(result.error || 'Failed to update PIN');
      }
    } catch (error) {
      toast.error('Failed to update PIN');
    } finally {
      setLoading(false);
    }
  };

  if (!internalUser) {
    return (
      <Card className="bg-gray-900/50 border border-gray-700">
        <CardContent className="p-6 text-center">
          <p className="text-gray-400">PIN functionality is only available for New School accounts.</p>
          <Button onClick={onBack} className="mt-4">
            Back
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-900/50 border border-gray-700">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onBack}
            className="text-gray-400 hover:text-white p-0 h-auto"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <CardTitle className="text-xl text-sports-green">
            Update PIN
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-pin" className="text-white">Current PIN</Label>
            <div className="relative">
              <Input
                id="current-pin"
                type={showCurrentPin ? 'text' : 'password'}
                value={formData.currentPin}
                onChange={(e) => setFormData({ ...formData, currentPin: e.target.value })}
                className="bg-gray-800 border-gray-600 text-white pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrentPin(!showCurrentPin)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-white"
              >
                {showCurrentPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-pin" className="text-white">New PIN</Label>
            <div className="relative">
              <Input
                id="new-pin"
                type={showNewPin ? 'text' : 'password'}
                value={formData.newPin}
                onChange={(e) => setFormData({ ...formData, newPin: e.target.value })}
                className="bg-gray-800 border-gray-600 text-white pr-12"
                minLength={4}
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPin(!showNewPin)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-white"
              >
                {showNewPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-pin" className="text-white">Confirm New PIN</Label>
            <div className="relative">
              <Input
                id="confirm-pin"
                type={showConfirmPin ? 'text' : 'password'}
                value={formData.confirmPin}
                onChange={(e) => setFormData({ ...formData, confirmPin: e.target.value })}
                className="bg-gray-800 border-gray-600 text-white pr-12"
                minLength={4}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPin(!showConfirmPin)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-white"
              >
                {showConfirmPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="flex-1 border-gray-600 text-white hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-sports-green text-black hover:bg-green-600"
            >
              {loading ? 'Updating...' : 'Update PIN'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default UpdatePinForm;
